import type Delta from "quill-delta";
import { STICKER_TEXT_FIT_THRESHOLD_PX } from "../sticker/constants";
import { MAX_SYMBOLS_IN_LINE, MIN_TEXT_WIDTH, SAFETY_PIXELS } from "./constantsV2";
import type { EditorCalc } from "./editorCalc";
import type { QuillInstance } from "./quillInstance";
import type { TextOverlay } from "./textOverlay";
import type {
    ResizeMode,
    ResizeOptions,
    ResizeResult,
    TextAlign,
    TextChangeMeta,
    TextVerticalAlign,
} from "./typesV2";
import { findBestStickerLayoutFromDelta, getStickerMinFontSize } from "./utilsV2";

export class TextResizer {
    private static readonly STICKER_SIZE_EPSILON = 0.5;
    private committedWidth = 0;
    private committedHeight = 0;
    private options: {
        minWidth: number;
        paddingX: number;
        paddingY: number;
        safety: number;
        emptyWidth: number;
        mode: ResizeMode;

        fixedWidth?: number;
        fixedHeight?: number;
        currentFontSize?: number;

        onlyLineMode?: boolean;
    };
    private hugeMeasureWidth = 6000;
    private stickerFitRafId: number | null = null;
    private pendingStickerFitReason: "text" | "resize" | null = null;
    private appliedStickerFitSignature: string | null = null;
    private stickerEditorResizeAnchor: {
        fixedWidth: number;
        fixedHeight: number;
        fontSize: number;
    } | null = null;
    private isFontSizeChangePropagating = false;
    private stickerInitialSize: { width: number; height: number } | null = null;
    private isStickerDirty = false;

    constructor(
        private quill: QuillInstance,
        private overlay: TextOverlay,
        private calc: EditorCalc,
        private onSize: ({ width, height, mode, fixedWidth }: ResizeResult) => void,
        private onFontSizeChange: (fontSize: number) => void,
        private getCurrentTextData: () => {
            fontFamily: string;
            fontSize: number;
            textAlign: TextAlign;
            textVerticalAlign: TextVerticalAlign;
            delta: Delta;
        },
    ) {
        this.options = {
            safety: SAFETY_PIXELS,
            paddingX: 0,
            paddingY: 0,
            emptyWidth: 0,
            minWidth: MIN_TEXT_WIDTH,
            mode: "auto",
            onlyLineMode: false,
        };
    }

    onTextChange = (_delta: Delta, meta: TextChangeMeta) => {
        if (this.quill.getLength() <= 1) {
            this.handleEmptyState();
            return;
        }

        switch (this.options.mode) {
            case "auto":
                this.handleAuto(meta);
                return;

            case "shape":
                this.measureFixedHeight();
                return;

            case "sticker":
                if (meta.source !== "user") {
                    return;
                }
                this.isStickerDirty = true;
                this.scheduleStickerFit("text");
                return;
        }
    };

    setResizeOptions(newOptions: ResizeOptions) {
        const prevMode = this.options.mode;
        this.options = { ...this.options, ...newOptions };

        if (this.options.mode === "sticker") {
            const width = Math.max(1, this.options.fixedWidth ?? this.committedWidth ?? 1);
            const height = Math.max(1, this.options.fixedHeight ?? this.committedHeight ?? 1);

            if (prevMode !== "sticker") {
                this.isStickerDirty = false;
                this.stickerInitialSize = { width, height };
            }
        } else {
            this.isStickerDirty = false;
            this.stickerInitialSize = null;
            this.stickerEditorResizeAnchor = null;
        }
    }

    remeasure() {
        if (this.isFontSizeChangePropagating) {
            return;
        }

        if (this.quill.getLength() <= 1) {
            this.handleEmptyState();
            return;
        }

        switch (this.options.mode) {
            case "auto": {
                const natural = this.measureNatural();
                const threshold = this.getAutoFixThreshold();

                if (natural.width + this.options.safety > threshold) {
                    this.applyShapeMeasurement(threshold);
                    return;
                }

                this.applyAutoMeasurement(false, natural);
                return;
            }

            case "shape":
                this.measureFixedHeight();
                return;

            case "sticker":
                this.scheduleStickerFit("resize");
                return;
        }
    }

    clear() {
        this.options = {
            safety: SAFETY_PIXELS,
            paddingX: 0,
            paddingY: 0,
            emptyWidth: 0,
            minWidth: MIN_TEXT_WIDTH,
            mode: "auto",
        };

        this.committedWidth = 0;
        this.committedHeight = 0;
        this.pendingStickerFitReason = null;
        this.appliedStickerFitSignature = null;
        this.stickerEditorResizeAnchor = null;
        this.stickerInitialSize = null;
        this.isStickerDirty = false;
        this.isFontSizeChangePropagating = false;

        if (this.stickerFitRafId !== null) {
            cancelAnimationFrame(this.stickerFitRafId);
            this.stickerFitRafId = null;
        }
    }

    private handleEmptyState() {
        const width = this.options.mode === "auto" ? this.options.emptyWidth : this.getFixedWidth();

        const { height } = this.measureWithWidth(width);

        this.commitChanges(width, height);
    }

    private getFixedWidth() {
        if (this.options.mode === "sticker") {
            return Math.max(1, this.options.fixedWidth ?? this.committedWidth ?? 1);
        }

        return Math.max(
            this.options.minWidth,
            this.options.fixedWidth ?? this.committedWidth ?? this.options.minWidth,
        );
    }

    private getAutoFixThreshold() {
        if (this.options.onlyLineMode) {
            return Number.POSITIVE_INFINITY;
        }

        //Примерно 85 символов в строке
        const widthMultiplicator = MAX_SYMBOLS_IN_LINE / 10;
        const autoFixThreshold = this.options.emptyWidth * widthMultiplicator;

        return Math.max(this.options.minWidth, autoFixThreshold);
    }

    private measureNatural() {
        const root = this.quill.getEditor() as HTMLElement;

        return this.calc.measure({
            html: root.innerHTML,
            styleSource: this.overlay.getStyleSourceEl(),
            fontFamily: this.overlay.getFontFamily(),
            minWidth: 0,
            maxWidth: Math.max(this.options.minWidth, this.hugeMeasureWidth),
            paddingX: this.options.paddingX,
            paddingY: this.options.paddingY,
        });
    }

    private measureWithWidth(width: number) {
        const root = this.quill.getEditor() as HTMLElement;

        return this.calc.measure({
            html: root.innerHTML,
            styleSource: this.overlay.getStyleSourceEl(),
            fontFamily: this.overlay.getFontFamily(),
            minWidth: width,
            maxWidth: width,
            paddingX: this.options.paddingX,
            paddingY: this.options.paddingY,
        });
    }

    private handleAuto(meta: TextChangeMeta) {
        if (meta.source !== "user") {
            this.applyAutoMeasurement(false);
            return;
        }

        const natural = this.measureNatural();
        const nextWidth = natural.width + this.options.safety;
        const threshold = this.getAutoFixThreshold();

        if (nextWidth > threshold) {
            this.applyShapeMeasurement(threshold);
            return;
        }

        const hasInsert = meta.insertedTextLength > 0;
        const hasDelete = meta.deletedLength > 0;
        const growOnly = hasInsert && !hasDelete;

        this.applyAutoMeasurement(growOnly, natural);
    }

    private applyAutoMeasurement(
        growOnly: boolean,
        measurement: { width: number; height: number } = this.measureNatural(),
    ) {
        const nextWidth = measurement.width + this.options.safety;

        const width = growOnly
            ? this.committedWidth === this.options.emptyWidth
                ? nextWidth
                : Math.max(this.committedWidth, nextWidth)
            : nextWidth;

        const height = growOnly
            ? Math.max(this.committedHeight, measurement.height)
            : measurement.height;

        this.commitChanges(width, height);
    }

    private applyShapeMeasurement(targetWidth: number) {
        const fixedWidth = Math.max(this.options.minWidth, targetWidth);
        const { height } = this.measureWithWidth(fixedWidth);

        this.options.mode = "shape";
        this.options.fixedWidth = fixedWidth;

        this.commitChanges(fixedWidth, height);
    }

    private measureFixedHeight() {
        const fixedWidth = this.getFixedWidth();
        const { height } = this.measureWithWidth(fixedWidth);

        this.commitChanges(fixedWidth, height);
    }

    private scheduleStickerFit(reason: "text" | "resize") {
        if (reason === "text") {
            this.stickerEditorResizeAnchor = null;
        }
        this.pendingStickerFitReason = reason;

        // Для первого введенного символа применяем fit синхронно:
        // rAF может отложить пересчет до следующего ввода и визуально кажется,
        // что размер скорректировался только на втором символе.
        if (reason === "text" && this.quill.getLength() <= 2) {
            if (this.stickerFitRafId !== null) {
                cancelAnimationFrame(this.stickerFitRafId);
                this.stickerFitRafId = null;
            }

            this.applyStickerFit(true);
            return;
        }

        if (this.stickerFitRafId !== null) {
            return;
        }

        this.stickerFitRafId = requestAnimationFrame(() => {
            this.stickerFitRafId = null;
            this.applyStickerFit();
        });
    }

    private getStickerFitSignature(reason: "text" | "resize"): string {
        const fixedWidth = this.getFixedWidth();
        const fixedHeight = this.options.fixedHeight ?? this.committedHeight;
        const currentFontSize = Math.max(1, Math.floor(this.options.currentFontSize ?? 0));
        const widthBucket =
            Math.round(fixedWidth / STICKER_TEXT_FIT_THRESHOLD_PX) * STICKER_TEXT_FIT_THRESHOLD_PX;
        const heightBucket =
            Math.round(fixedHeight / STICKER_TEXT_FIT_THRESHOLD_PX) * STICKER_TEXT_FIT_THRESHOLD_PX;
        const contentSignature =
            reason === "text" ? (this.quill.getEditor() as HTMLElement).innerHTML : "";

        if (reason === "text") {
            return `text:${widthBucket}:${heightBucket}:${currentFontSize}:${contentSignature}`;
        }

        return `resize:${widthBucket}:${heightBucket}:${currentFontSize}`;
    }

    private applyStickerFit(force = false) {
        const reason = this.pendingStickerFitReason;
        if (!reason) {
            return;
        }

        const signature = this.getStickerFitSignature(reason);
        if (!force && signature === this.appliedStickerFitSignature) {
            return;
        }

        this.pendingStickerFitReason = null;
        this.measureAndFitSticker(reason);
        this.appliedStickerFitSignature = signature;
    }

    flushStickerFit() {
        if (this.stickerFitRafId !== null) {
            cancelAnimationFrame(this.stickerFitRafId);
            this.stickerFitRafId = null;
        }

        if (this.pendingStickerFitReason) {
            this.applyStickerFit(true);
        }
    }

    private measureAndFitSticker(reason: "text" | "resize") {
        if (!this.options.currentFontSize) {
            return;
        }

        const fixedWidth = this.getFixedWidth();
        const fixedHeight = this.options.fixedHeight ?? this.committedHeight;

        if (reason === "resize" && !this.isStickerDirty && this.stickerInitialSize) {
            const widthChanged =
                Math.abs(fixedWidth - this.stickerInitialSize.width) >
                TextResizer.STICKER_SIZE_EPSILON;
            const heightChanged =
                Math.abs(fixedHeight - this.stickerInitialSize.height) >
                TextResizer.STICKER_SIZE_EPSILON;

            if (!widthChanged && !heightChanged) {
                return;
            }
        }

        if (reason === "resize") {
            if (
                !this.stickerEditorResizeAnchor ||
                this.stickerEditorResizeAnchor.fixedWidth <= 0 ||
                this.stickerEditorResizeAnchor.fixedHeight <= 0
            ) {
                this.stickerEditorResizeAnchor = {
                    fixedWidth: Math.max(1, fixedWidth),
                    fixedHeight: Math.max(1, fixedHeight),
                    fontSize: Math.max(1, this.options.currentFontSize),
                };
            }

            const anchor = this.stickerEditorResizeAnchor;
            const scaleByWidth = fixedWidth / anchor.fixedWidth;
            const scaleByHeight = fixedHeight / anchor.fixedHeight;
            const scale = Math.max(0, Math.min(scaleByWidth, scaleByHeight));
            const scaledFontSize = Math.max(1, anchor.fontSize * scale);

            const currentTextData = this.getCurrentTextData();
            const minFontSize = getStickerMinFontSize(fixedWidth, fixedHeight);
            const resizedLayout = findBestStickerLayoutFromDelta({
                data: {
                    ...currentTextData,
                    fontSize: scaledFontSize,
                },
                hardWidth: fixedWidth,
                hardHeight: fixedHeight,
                minFontSize,
                maxFontSize: Math.max(
                    minFontSize,
                    fixedHeight,
                    fixedWidth,
                    this.options.currentFontSize,
                    scaledFontSize,
                ),
            });

            if (this.options.currentFontSize !== resizedLayout.fontSize) {
                this.options.currentFontSize = resizedLayout.fontSize;
                this.isFontSizeChangePropagating = true;
                this.onFontSizeChange(resizedLayout.fontSize);
                this.isFontSizeChangePropagating = false;
            }

            this.commitChanges(
                fixedWidth,
                Math.max(1, Math.min(fixedHeight, resizedLayout.height)),
            );
            return;
        }

        const currentTextData = this.getCurrentTextData();
        const minFontSize = getStickerMinFontSize(fixedWidth, fixedHeight);
        const bestLayout = findBestStickerLayoutFromDelta({
            data: {
                ...currentTextData,
                delta: this.quill.getDelta(),
            },
            hardWidth: fixedWidth,
            hardHeight: fixedHeight,
            minFontSize,
            maxFontSize: Math.max(
                minFontSize,
                fixedHeight,
                fixedWidth,
                this.options.currentFontSize,
            ),
        });

        if (this.options.currentFontSize !== bestLayout.fontSize) {
            this.options.currentFontSize = bestLayout.fontSize;
            this.isFontSizeChangePropagating = true;
            this.onFontSizeChange(bestLayout.fontSize);
            this.isFontSizeChangePropagating = false;
        }

        this.commitChanges(fixedWidth, bestLayout.height);
    }

    private commitChanges(width: number, height: number) {
        if (this.committedWidth === width && this.committedHeight === height) {
            return;
        }

        this.committedWidth = width;
        this.committedHeight = height;

        this.onSize({
            width,
            height,
            mode: this.options.mode,
            fixedWidth: this.options.mode === "shape" ? this.getFixedWidth() : undefined,
        });
    }
}
